import { _ } from "../polyglot/polyglot.js";
import { calendarIcon } from "../elements/icons/index.js";
import { formatAsDateAndTime } from "../helpers/date-formatting-util.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import chartsOfAccountsSectionWidget from "../../accounting/companySettings/chartsOfAccountsSectionWidget.js";
import CompanyCapegoIntegrationWidget from "../widgets/CompanyCapegoIntegrationWidget/CompanyCapegoIntegrationWidget.js";
import CompanyInfoFormWidget from "../widgets/CompanyInfoFormWidget.js";
import editPreliminaryDocumentsWidget from "../../accounting/companySettings/editPreliminaryDocumentsWidget.js";
import fiscalYearImportWidget from "../widgets/fiscalYear/fiscalYearImportWidget.js";
import fiscalYearListWidget from "../widgets/fiscalYear/fiscalYearListWidget.js";
import ForecastTemplateConfigurationWidget from "../../accounting/companySettings/ForecastTemplateConfigurationWidget/ForecastTemplateConfigurationWidget.js";
import KeyFigurePresetService from "../services/KeyFigurePresetService.js";
import KeyFigureSettingsDocumentSection from "./KeyFigureSettingsDocumentSection.js";
import ModelsSettingsDocumentSection from "./ModelsSettingsDocumentSection/ModelsSettingsDocumentSection.js";
import notifier from "../widgets/notifier/notifier.js";
import nullPeriod from "../../models/period/nullPeriod.js";
import optionsBag from "../../models/options/optionsBag.js";
import OwnKeyFigureSettingsService from "../services/OwnKeyFigureSettingsService.js";
import PackagesTableWidget from "../widgets/PackagesTableWidget/PackagesTableWidget.js";
import ReportTemplateSettingsService from "../services/ReportTemplateSettingsService.js";
import sectionWidget from "../widgets/sectionWidget.js";
import session from "../session.js";
import TabWidget from "../widgets/TabWidget/TabWidget.js";
import visibleKeyFigures from "../../models/keyFigure/preset/visibleKeyFigures.js";

/**
 * `CompanySettingsWidget` gathers all the sections displayed in company settings,
 * both in Accounting and Agency.
 *
 * @param {user} currentUser - User currently logged in
 * @param {company} company - Company being browsed
 * @param {boolean} showChartOfAccountsEditor - `true` if "Chart of Accounts"
 * section must be displayed
 * @param {function(ChartOfAccount):boolean} canDeleteChartOfAccount - Returns true if the chartOfAccount can be deleted
 * @param {boolean} showCustomKeyFigures - `true` if "Custom KPIs" section
 * must be displayed.
 * @param {boolean} accountMappingEnabled - `true` if links to the account
 * mapping UI can be displayed.
 * @param {boolean} isDocumentsEnabled - `true` if "Preliminary documents"
 * section must be displayed
 * @param {boolean} isManageUsersInUiDisabled - `true` if "Users"
 * section must be displayed
 * @param {boolean} isArchiveCompanyEnabled - `true` if the archiving button
 * must be displayed
 * @param {Repository} knownReportTemplateRepository - To fetch the
 * company's known report templates.
 * @param {widget} usersWidget - Widget used to render the company users
 * @param {widget} [consultantsWidget] - Widget used to render the consultants
 * @param {documentService} [documentService] - An instance
 * of the service responsible for manipulating the documents of
 * `company`
 * @param {companyTypes[]} [companyTypes] - List of company
 * types to choose from for the company
 * @param {function} [onPackageSelection] - A Function to perform specific actions when a Package
 * is selected. Takes a Package Id and a callback function as parameters. Selection is disabled if
 * this function is falsy.
 * @param {KeyFigureService} keyFigureService - To take care of manipulating KPIs.
 * @param {function} onEditKeyFigurePreset - Function called with a
 * key figure preset as argument.
 * @param {function(ChartOfAccount):boolean} isAgencyChartOfAccounts - Returns true if the chartOfAccount is owned by the agency
 * @param {boolean} [capegoSectionEnabled] - Pass `true` if the
 *   "Capego Connection" section must be visible
 * @param {CompanyIntegrationSettings} integrationSettings - Specifies
 *   how a company is integrated with data providers
 * @param {function} [onIntegrationSettingsChange] - Pass a function
 *   to be called each time the user changes the company's integration
 *   settings
 */
export default class CompanySettingsDocumentSection extends Widget {
	constructor(spec) {
		super(spec);
		this._currentUser = spec.currentUser;
		this._company = spec.company;
		this._country = spec.country;
		this._showChartOfAccountsEditor = spec.showChartOfAccountsEditor;
		this._canDeleteChartOfAccounts = spec.canDeleteChartOfAccounts;
		this._isAgencyChartOfAccounts = spec.isAgencyChartOfAccounts;
		this._showCustomKeyFigures = spec.showCustomKeyFigures;
		this._isDocumentsEnabled = spec.isDocumentsEnabled;
		this._isForecastingEnabled = spec.isForecastingEnabled;
		this._isArchiveCompanyEnabled = spec.isArchiveCompanyEnabled;
		this._isManageUsersInUiDisabled = spec.isManageUsersInUiDisabled;
		this._capegoSectionEnabled = spec.capegoSectionEnabled ?? false;
		this._integrationSettings = spec.integrationSettings;
		this._onIntegrationSettingsChange = spec.onIntegrationSettingsChange;

		this._knownReportTemplateRepository =
			spec.knownReportTemplateRepository;

		this._usersWidget = spec.usersWidget;
		this._consultantsWidget = spec.consultantsWidget;
		this._documentService = spec.documentService;

		this._companyTypes = spec.companyTypes;
		this._accountMappingEnabled = spec.accountMappingEnabled;
		this._onPackageSelection = spec.onPackageSelection;
		this._onEditKeyFigurePreset = spec.onEditKeyFigurePreset;

		this._keyFigureService = spec.keyFigureService;

		this._keyFigurePresetService = new KeyFigurePresetService({
			companyId: this._company.getLegacyId(),
			keyFigureService: this._keyFigureService,
		});

		this._createEvents(
			"onBudgetDeleted",
			"onChartOfAccountsClicked",
			"onChartOfAccountsDeleted",
			"onChartOfAccountsImported",
			"onCompanySaved",
			"onCompanyDeleted",
			"onCompanyDataExtractionRequested",
			"onFiscalYearDeleted",
			"onFiscalYearSaved",
			"onUserCreated",
			"onUserDeleted",
			"onIntegrationDeleted",
			"onCompanyArchived",
			"customKeyFigureRemoved",
		);

		this._chartsOfAccountsSectionWidget = chartsOfAccountsSectionWidget({
			company: this._company,
			chartOfAccountsEditorEnabled: this._showChartOfAccountsEditor,
			accountMappingEnabled: this._accountMappingEnabled,
			canDeleteChartOfAccounts: this._canDeleteChartOfAccounts.bind(this),
			isAgencyChartOfAccounts: this._isAgencyChartOfAccounts.bind(this),
		});

		this._chartsOfAccountsSectionWidget.onChartOfAccountsImported.register(
			this.onChartOfAccountsImported.trigger,
		);

		this._chartsOfAccountsSectionWidget.onChartOfAccountsRemoved.register(
			(coa) => {
				this.onChartOfAccountsDeleted.trigger(
					coa,
					() => {
						this._chartsOfAccountsSectionWidget.succeed();
					},
					({ chartsOfAccounts, error }) => {
						notifier.error(
							this._getErrorMessage({
								chartOfAccounts: coa,
								error,
							}),
						);

						// Refresh the list
						this._chartsOfAccountsSectionWidget.setChartsOfAccounts(
							chartsOfAccounts,
						);

						this._fiscalYearList.setChartsOfAccounts(
							chartsOfAccounts,
						);
					},
				);
			},
		);

		this._chartsOfAccountsSectionWidget.onChartOfAccountsClicked.register(
			(chartOfAccounts) => {
				this.onChartOfAccountsClicked.trigger(chartOfAccounts);
			},
		);

		this._fiscalYearList = fiscalYearListWidget({
			fiscalYears: this._getAllFiscalYears(),
		});

		const integrationTypes =
			this._country && this._country.getPreferredIntegrations();

		this._fiscalYearImport = fiscalYearImportWidget({
			company: this._company,
			abortUrl: encodeURIComponent(
				`${window.location.pathname}#!${this._router
					.getUrl()
					.getPath()}`,
			),
			returnUrl: encodeURIComponent(
				`${window.location.pathname}#!${this._router
					.getUrl()
					.getPath()}`,
			),
			integrationTypes,
		});

		this._fiscalYearList.onDeleteFiscalYear.register(
			this._showDeleteConfirmationDialog.bind(this),
		);

		this._fiscalYearList.onDeleteBudget.register(
			this._showBudgetDeleteConfirmationDialog.bind(this),
		);

		this._fiscalYearList.onSaveFiscalYear.register(
			this.onFiscalYearSaved.trigger,
		);

		this._usersWidget.onRemoveUser.register((userToRemove) => {
			this.onUserDeleted.trigger(userToRemove);
		});

		this._usersWidget.onCreateUser.register((userToCreate) => {
			this.onUserCreated.trigger(userToCreate);
		});

		this._editCompanyDetailsWidget = new CompanyInfoFormWidget({
			company: this._company,
			currentUser: this._currentUser,
			companyTypes: this._companyTypes,
			isArchiveCompanyEnabled: this._isArchiveCompanyEnabled,
		});

		this._editCompanyDetailsWidget.onSave.register(
			this.onCompanySaved.trigger,
		);

		this._editCompanyDetailsWidget.onDelete.register(
			this.onCompanyDeleted.trigger,
		);

		this._editCompanyDetailsWidget.onArchive.register(
			this.onCompanyArchived.trigger,
		);

		this._editCompanyDetailsWidget.onDataExtractionRequested.register(
			this.onCompanyDataExtractionRequested.trigger,
		);

		this._ownKeyFigureSettingsService = new OwnKeyFigureSettingsService({
			service: this._keyFigureService,
			owner: this._company,
		});

		this._systemKeyFigureSettings = new KeyFigureSettingsDocumentSection({
			ownKeyFigureSettingsService: this._ownKeyFigureSettingsService,
			onKeyFigureCreated: (keyFigure) => this._saveKeyFigure(keyFigure),
			canEditKeyFigures: this._canEditKeyFigures.bind(this),
			canDeleteKeyFigure: (keyFigure) =>
				this._currentUser.canDeleteKeyFiguresFor(this._company) &&
				keyFigure.canBeDeletedBy(this._company),
			viewKeyFigureCallback: spec.viewKeyFigureCallback,
			redirectToKeyFigureEditor: spec.redirectToKeyFigureEditor,
		});

		this._systemKeyFigureSettings.onPresetCreated.register((preset) =>
			this._keyFigurePresetService
				.save(preset)
				.then(() => this._onEditKeyFigurePreset(preset)),
		);

		this._systemKeyFigureSettings.onPresetClicked.register((preset) =>
			this._onEditKeyFigurePreset(preset),
		);

		this._systemKeyFigureSettings.onVisibleKeyFiguresChange.register(
			(preset) => this._saveKeyFigurePreset(preset),
		);

		this._systemKeyFigureSettings.customKeyFigureRemoved.register(
			(keyFigure) =>
				this._keyFigureService
					.remove(keyFigure)
					.then(() => this._updateAllKeyFiguresFromCache()),
		);

		this._forecastTemplateConfigurationWidget =
			new ForecastTemplateConfigurationWidget({
				companyId: this._company.getLegacyId(),
			});

		this._editPreliminaryDocumentsWidget = editPreliminaryDocumentsWidget({
			companyId: this._company.getLegacyId(),
		});

		this._editPreliminaryDocumentsWidget.onMarkDocument.register(
			(document, callback) => {
				this._documentService.updateIsPreliminary({
					entry: document,
					isPreliminary: false,
					onSuccess: () => {
						callback();

						this._documentService.resetCache();
						this._populateEditPreliminaryDocumentsWidget();
					},
				});
			},
		);

		this._editPreliminaryDocumentsWidget.onMarkAllDocuments.register(
			(selection, callback) => {
				selection.forEach(
					(document, fn) => {
						this._documentService.updateIsPreliminary({
							entry: document,
							isPreliminary: false,
							onSuccess: fn,
						});
					},
					() => {
						callback();

						this._documentService.resetCache();
						this._populateEditPreliminaryDocumentsWidget();
					},
				);
			},
		);

		this._populateEditPreliminaryDocumentsWidget();

		if (this._knownReportTemplateRepository) {
			this._reportTemplatesSettings = new ModelsSettingsDocumentSection({
				service: new ReportTemplateSettingsService({
					ownerType: "Company",
					owner: this._company,
					repository: this._knownReportTemplateRepository,
				}),
			});
		}

		this._initializeTabWidget();
	}

	show() {
		this._keyFigureService
			.populateCache()
			.then(() =>
				Promise.all([
					this._fetchAllKeyFigurePresets(),
					this._updateAllKeyFiguresFromCache(),
				]),
			);
	}

	setUsers(companyUsers) {
		this._usersWidget.setUsers(companyUsers);
	}

	setConsultants(consultants) {
		if (!this._consultantsWidget) {
			return;
		}
		this._consultantsWidget.setUsers(consultants);
	}

	setChartsOfAccounts(data) {
		this._chartsOfAccountsSectionWidget.setChartsOfAccounts(data);
		this._fiscalYearList.setChartsOfAccounts(data);
		this.update();
	}

	setForecastTemplateConfiguration(configuration) {
		this._forecastTemplateConfigurationWidget.setConfiguration(
			configuration,
		);

		this.update();
	}

	setDocuments(documents) {
		this._editPreliminaryDocumentsWidget.setDocuments(documents);
	}

	setPackages({ packages, selectionId }) {
		this._packagesWidget =
			packages && packages.length > 0
				? new PackagesTableWidget({
						packages,
						selectionId,
						canUserForcePackageSelection:
							this._currentUser.canForcePackageSelection(),
						onSelection: this._onPackageSelection,
					})
				: undefined;

		this.update();
	}

	updateReportTemplates() {
		this._reportTemplatesSettings && this._reportTemplatesSettings.show();
	}

	renderContentOn(html) {
		html.render(this._tabsWidget);
	}

	renderTopActionsOn(html) {
		if (
			this._company.getCurrentImportIntegration().isEconomicIntegration()
		) {
			return;
		}

		html.a({
			id: "import-btn",
			href: this._fiscalYearImport.linkToCurrentImport(),
		});
	}

	_renderCompanyDetailsOn(html) {
		html.div(
			{ klass: "row" },
			html.div(
				{
					klass: "col col-md-6 col-sm-12",
					"data-help": "company-info",
				},
				html.h2({ klass: "section-title" }, _("Company info")),
				this._editCompanyDetailsWidget,
			),
			html.div(
				{
					klass: "col col-md-6 col-sm-12",
				},
				html.div(
					{ "data-help": "fiscal-years" },
					html.h2({ klass: "section-title" }, _("Fiscal years")),
					this._renderFiscalYearSectionOn.bind(this),
				),
				(html) => {
					if (!this._capegoSectionEnabled) return;

					html.div(
						{ "data-help": "capego-connection" },
						html.h2(
							{ class: "section-title" },
							_("Capego connection"),
						),
						this._renderIntegrationSettingsOn.bind(this),
					);
				},
			),
		);
	}

	_renderChartOfAccountsOn(html) {
		html.render(
			sectionWidget({
				cssClass: "col-lg-6",
				titleCssClass: "section-title",
				title: _("Chart of Accounts"),
				content: this._chartsOfAccountsSectionWidget,
			}),
		);
	}

	_renderForecastTemplateConfigurationOn(html) {
		html.render(
			sectionWidget({
				cssClass: "col-lg-12",
				titleCssClass: "section-title",
				title: _("Default Forecast template configuration"),
				content: this._forecastTemplateConfigurationWidget,
			}),
		);
	}

	_renderReportTemplatesOn(html) {
		if (!this._reportTemplatesSettings) {
			return;
		}

		html.div(
			{ klass: "row" },
			html.div(
				{
					class: "col col-md-6 col-sm-12",
					"data-test": "default-templates-section",
				},
				html.h2({ klass: "section-title" }, _("Report templates")),
				this._reportTemplatesSettings,
			),
		);
	}

	_renderUsersAndConsultantsOn(html) {
		html.div(
			{ klass: "row" },
			html.div(
				{ klass: "col col-sm-12" },
				(html) => this._renderCompanyUsersOn(html),
				(html) => this._renderAgencyUsersOn(html),
			),
		);
	}

	_renderCompanyUsersOn(html) {
		html.h2({ klass: "section-title" }, _("Client users"));
		html.render(this._usersWidget);
	}

	_renderAgencyUsersOn(html) {
		if (!this._canShowConsultantsWidget()) {
			return;
		}
		html.h2({ klass: "section-title" }, _("Consultants"));
		html.render(this._consultantsWidget);
	}

	_renderPreliminaryDocumentsOn(html) {
		html.div(
			{ klass: "row" },
			html.div(
				{ klass: "col col-sm-12" },
				html.h2({ klass: "section-title" }, _("Preliminary documents")),
				this._editPreliminaryDocumentsWidget,
			),
		);
	}

	_renderPackageOn(html) {
		html.div(
			{ klass: "row" },
			html.div(
				{ klass: "col col-sm-12" },
				html.h2({ klass: "section-title" }, _("Packages")),
				this._packagesWidget,
			),
		);
	}

	_renderFiscalYearSectionOn(html) {
		if (!this._currentUser.canImportTo(this._company)) {
			return;
		}

		const lastImportDate = this._company.getLastImportDate()
			? formatAsDateAndTime(this._company.getLastImportDate())
			: "";

		html.dl(
			{ klass: "last-import-date import-action" },
			html.dt(
				calendarIcon({ class: "import-action" }),
				_("Last import date"),
			),
			html.dd(lastImportDate),
		);

		html.render(this._fiscalYearList);

		html.div(
			{ class: "clearfix" },
			html.div(
				{ class: "pull-right" },
				(html) => this._renderDisconnectIntegrationOn(html),
				this._fiscalYearImport,
			),
		);
	}

	_renderIntegrationSettingsOn(html) {
		const settings =
			this._integrationSettings.getCapegoIntegrationSettings();

		html.render(
			new CompanyCapegoIntegrationWidget({
				settings,
				onSettingsChange: () => this._onIntegrationSettingsChange(),
			}),
		);
	}

	_renderDisconnectIntegrationOn(html) {
		if (!this._company.getCurrentImportIntegration().canBeDisconnected()) {
			return;
		}

		html.a(
			{
				klass: "btn btn-default import-action",
				"data-test": "disconnect-integration",
				click: this._disconnectCurrentIntegration.bind(this),
			},
			_("Disconnect"),
		);
	}

	_disconnectCurrentIntegration() {
		const integration = this._company.getCurrentImportIntegration();

		this._confirmDeletion({
			name: _("the integration credentials and disconnect from {name}", {
				name: integration.getName(),
			}),
			callback: () => {
				this._company.removeIntegration(integration);
				this.onIntegrationDeleted.trigger(integration);
				this._company.setDefaultCurrentImportIntegration();
				this.update();
			},
		});
	}

	_renderKPIsOn(html) {
		html.div(
			{ klass: "row" },
			html.div(
				{ klass: "col col-md-12 col-sm-12" },
				this._systemKeyFigureSettings,
			),
		);
	}

	_canShowConsultantsWidget() {
		return (
			this._consultantsWidget &&
			!this._isManageUsersInUiDisabled &&
			this._currentUser.canManageUsersFor(this._company)
		);
	}

	_showDeleteConfirmationDialog(year) {
		this._confirmDeletion({
			name: _("fiscal year {year}", {
				year: year.toSmartString(),
			}),
			body: _(
				"Are you sure you want to permanently delete the fiscal year {year}?{br}By doing so, you will also delete all budgets related to that year.",
				{ year: year.toSmartString(), br: (html) => html.br() },
			),
			callback: () => this._removeFiscalYear(year),
		});
	}

	_showBudgetDeleteConfirmationDialog(budget, fiscalYear, callback) {
		this._confirmDeletion({
			name: _("budget {budget}", { budget: budget.getName() }),
			callback: () => this._removeBudget(budget, fiscalYear, callback),
		});
	}

	_removeFiscalYear(year) {
		this.onFiscalYearDeleted.trigger(year, () => {
			this._company.removeYear(year);

			notifier.success(
				_("The fiscal year of {year} is removed successfully", {
					year: year.toSmartString(),
				}),
			);

			if (!this._company.getActiveYears().length) {
				session.redirectToPeriod(nullPeriod);
				return;
			}

			if (session.getPeriod().getYear().equals(year)) {
				// If we remove the year we are currently in
				// set the current period to the last month of the last fiscal year
				session.redirectToPeriod(
					this._company.getCurrentYear().lastMonth(),
				);

				return;
			}

			this._fiscalYearList.setFiscalYears(this._getAllFiscalYears());
		});
	}

	_getAllFiscalYears() {
		return this._company.getYears();
	}

	_removeBudget(budget, fiscalYear, callback) {
		this.onBudgetDeleted.trigger(budget, this._company, () => {
			let budgets = fiscalYear.getBudgets();

			budgets = budgets.filter(
				(aBudget) => aBudget.getId() !== budget.getId(),
			);

			fiscalYear.setBudgets(budgets);
			callback();
		});
	}

	_initializeTabWidget() {
		this._tabsWidget = new TabWidget({
			tabs: [
				{
					name: _("Company details"),
					id: "tab-company-info",
					content: (html) => this._renderCompanyDetailsOn(html),
					enabled: () =>
						this._currentUser.canEditCompanyInfoFor(this._company),
				},
				{
					name: _("Client users & consultants"),
					id: "tab-users",
					dataTestName: "users",
					content: (html) => this._renderUsersAndConsultantsOn(html),
					enabled: () =>
						!this._isManageUsersInUiDisabled &&
						this._currentUser.canManageUsersFor(this._company),
				},
				{
					name: _("Chart of Accounts"),
					id: "tab-chart-of-accounts",
					content: (html) => this._renderChartOfAccountsOn(html),
					enabled: () =>
						this._currentUser.canEditChartOfAccountFor(
							this._company,
						),
				},
				{
					name: _("KPIs"),
					id: "tab-KPIs",
					content: (html) => this._renderKPIsOn(html),
					enabled: () =>
						this._currentUser.canEditPresetsFor(this._company),
				},
				{
					name: _("Report templates"),
					id: "tab-classic-report",
					dataTestName: "report-templates",
					content: this._renderReportTemplatesOn.bind(this),
					enabled: () => this._reportTemplatesSettings,
				},
				{
					name: _("Preliminary Documents"),
					id: "tab-preliminary-documents",
					content: (html) => this._renderPreliminaryDocumentsOn(html),
					enabled: () =>
						this._isDocumentsEnabled &&
						this._currentUser.canEditPreliminaryDocuments(
							session.getCompany(),
						),
				},
				{
					name: _("Packages"),
					id: "tab-packages",
					content: (html) => this._renderPackageOn(html),
					enabled: () => this._packagesWidget,
				},
				{
					name: _("Forecast template configuration"),
					id: "tab-forecast",
					content: (html) =>
						this._renderForecastTemplateConfigurationOn(html),
					enabled: () =>
						this._isForecastingEnabled &&
						this._currentUser.canAdministrateForecastsFor(
							session.getCompany(),
						),
				},
			],
			tabsOnlyOnMobile: false,
			hideLabelsOnMobile: true,
		});
	}

	_populateEditPreliminaryDocumentsWidget() {
		if (!this._isDocumentsEnabled) {
			return;
		}

		this._documentService.loadEntries(() => {
			const preliminaryDocuments = this._documentService
				.getRoot()
				.findPreliminaryChildren();

			this._editPreliminaryDocumentsWidget.setDocuments(
				preliminaryDocuments,
			);
		});
	}

	_canEditKeyFigures() {
		if (!optionsBag.fromId("keyfigure-editor").isActive()) {
			return false;
		}

		return this._currentUser.canEditKeyFiguresFor(this._company);
	}

	_fetchAllKeyFigurePresets() {
		return this._keyFigurePresetService
			.findAll()
			.then((allKeyFigurePresets) =>
				this._systemKeyFigureSettings.setPresets(allKeyFigurePresets),
			);
	}

	_updateAllKeyFiguresFromCache() {
		return this._keyFigureService
			.getAllFromCache()
			.then((allKeyFigures) =>
				this._systemKeyFigureSettings.setAllKeyFigures(allKeyFigures),
			);
	}

	_saveKeyFigure(keyFigure) {
		return this._keyFigureService
			.create(keyFigure)
			.then(() => this._addKeyFigureToVisiblePreset(keyFigure))
			.then(() => this._updateAllKeyFiguresFromCache());
	}

	_addKeyFigureToVisiblePreset(keyFigure) {
		visibleKeyFigures.addKeyFigure(keyFigure);

		return this._saveKeyFigurePreset(visibleKeyFigures);
	}

	_saveKeyFigurePreset(preset) {
		return this._keyFigurePresetService
			.update(preset)
			.then(() => this._fetchAllKeyFigurePresets());
	}

	_getErrorMessage({ chartOfAccounts, error }) {
		if (
			error?.responseJSON?.message ===
			"ChartOfAccountInUseInOtherCompanies"
		)
			return _(
				"Cannot delete this chart of accounts as it is in use in other companies or existing fiscal years",
			);

		return _("Failed to delete {name}", {
			name: chartOfAccounts.getName(),
		});
	}
}
